import BaseMapController from "./base_map_controller"
import { MapBounds, BaseMarker } from "@/utils/map_interfaces"
import { Turbo } from "@hotwired/turbo-rails"

interface InteractionMarker extends BaseMarker {
  info: {
    type: string
    created_at: string
    staff_name: string
    organization_name: string
  }
}

export default class extends BaseMapController {
  static targets = ["map"]
  static values = {
    ...BaseMapController.values,
    defaultLat: { type: Number, default: 39.8283 },
    defaultLng: { type: Number, default: -98.5795 }
  }

  declare readonly defaultLatValue: number
  declare readonly defaultLngValue: number

  protected async fetchMarkers(bounds?: MapBounds) {
    // The markersUrlValue already contains the filters and search query from the view
    // We just need to add the map bounds if available
    const url = new URL(this.markersUrlValue, window.location.origin)

    if (bounds) {
      Object.entries(bounds).forEach(([key, value]) => {
        url.searchParams.set(key, value.toString())
      })
    }

    // Make sure format is set to json if not already included
    if (!url.searchParams.has('format')) {
      url.searchParams.set('format', 'json')
    }

    const response = await fetch(url)
    const markers: InteractionMarker[] = await response.json()

    this.clearMarkers()
    this.renderMarkers(markers)

    // Fit the map to the markers if we have any
    if (markers.length > 0) {
      this.fitMapToMarkers(markers)
    } else {
      // If no markers are found and we're not using bounds,
      // revert to default location or a wider view
      if (!bounds) {
        this.resetMapView()
      }
    }
  }

  protected resetMapView() {
    if (!this.map) return

    this.map.setCenter({
      lat: this.defaultLatValue,
      lng: this.defaultLngValue
    })
    this.map.setZoom(4) // Wider view
  }

  protected fitMapToMarkers(markers: InteractionMarker[]) {
    if (!this.map || markers.length === 0) return

    const bounds = new google.maps.LatLngBounds()

    markers.forEach(marker => {
      bounds.extend({
        lat: marker.latitude,
        lng: marker.longitude
      })
    })

    if (this.mapMoved) {
      // Use panToBounds for smoother transitions when there are existing markers
      this.map.panToBounds(bounds, 50) // Add some padding

      // Smoothly zoom if needed
      const currentZoom = this.map.getZoom() || 0
      const idealZoom = this.calculateIdealZoom(bounds)

      if (idealZoom && Math.abs(currentZoom - idealZoom) > 2) {
        // Only adjust zoom if there's a significant difference
        setTimeout(() => {
          this.map?.setZoom(idealZoom)
        }, 300) // Slight delay for smoother transition after panning
      }
    } else {
      // First load - just fit bounds without animation
      this.map.fitBounds(bounds)

      // If we only have one marker, zoom to a reasonable level
      if (markers.length === 1) {
        this.map.setZoom(Math.min(15, this.map.getZoom() || 15))
      }
    }
  }

  protected calculateIdealZoom(bounds: google.maps.LatLngBounds): number {
    // Default to zoom level 15 for small bounds
    if (!this.map) return 15

    const ne = bounds.getNorthEast()
    const sw = bounds.getSouthWest()

    // Calculate distance between bounds corners (rough approximation)
    const latDiff = Math.abs(ne.lat() - sw.lat())
    const lngDiff = Math.abs(ne.lng() - sw.lng())
    const maxDiff = Math.max(latDiff, lngDiff)

    // Determine appropriate zoom level based on the bounds size
    if (maxDiff > 1) return 8       // Very large area
    if (maxDiff > 0.5) return 10     // Large area
    if (maxDiff > 0.1) return 12     // Medium area
    if (maxDiff > 0.05) return 13    // Small area
    if (maxDiff > 0.01) return 14    // Very small area
    return 15                        // Tiny area
  }

  protected createMarkerIcon<T extends BaseMarker>(markerData: T): google.maps.Icon {
    // Use PNG image for marker pins
    return {
      url: '/marker-pin.png',
      scaledSize: new google.maps.Size(30, 40),
      anchor: new google.maps.Point(15, 20) // Adjust anchor point to bottom center of the pin
    }
  }

  protected handleMarkerClick<T extends BaseMarker>(markerData: T): () => void {
    return () => {
      // Open interaction in drawer frame
      const neighborId = (this.element as HTMLElement).getAttribute('data-neighbor-id')
      Turbo.visit(`/neighbors/${neighborId}/interactions/${markerData.id}`, { frame: 'drawer_frame' })
    }
  }
}
